import { useState, useEffect, useRef } from "react";
import { useSingleton } from "../../hooks/use-singleton";
import assets from "../../assets";
import "./styles.sass";

const STAGES = [
  { key: "INTRODUCTION", label: "Introduction" },
  { key: "DAF_DEEP_DIVE", label: "DAF Deep Dive" },
  { key: "CURRENT_AFFAIRS", label: "Current Affairs" },
  { key: "SITUATIONAL", label: "Situational" },
  { key: "COUNTER", label: "Counter" },
  { key: "FINAL", label: "Final" },
];

export const UpscInterview = () => {
  const { user, mail: singletonMail } = useSingleton();

  // ── States ───────────────────────────────────────────────────────
  const [baseUrl, setBaseUrl] = useState(
    "https://visal1chatbotvm.southindia.cloudapp.azure.com/secure-api/"
  );
  
  // Profile Form States
  const [profile, setProfile] = useState({
    mail: "",
    candidateName: "",
    hometown: "",
    graduationSubject: "",
    optionalSubject: "",
    workExperience: "",
    hobbies: "",
  });

  // Prefill user details from hook
  useEffect(() => {
    if (singletonMail) {
      setProfile((prev) => {
        const rawName = prev.candidateName || user?.data?.name || singletonMail.split("@")[0];
        const cleanName = rawName.split(/[._-]/)[0];
        const formattedName = cleanName.charAt(0).toUpperCase() + cleanName.slice(1);
        return {
          ...prev,
          mail: prev.mail || singletonMail,
          candidateName: formattedName,
        };
      });
    }
  }, [singletonMail, user]);

  // Session & Progress States
  const [sessionId, setSessionId] = useState(null);
  const [copied, setCopied] = useState(false);
  const [currentStage, setCurrentStage] = useState(null);
  const [interviewActive, setInterviewActive] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [questionCount, setQuestionCount] = useState(0);
  
  // Chat States
  const [messages, setMessages] = useState([]);
  const [currentAnswer, setCurrentAnswer] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [hasSpoken, setHasSpoken] = useState(false); // voice-first gate

  // Audio States
  const [isMuted, setIsMuted] = useState(false);
  const [currentPlayingId, setCurrentPlayingId] = useState(null);

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [evalData, setEvalData] = useState(null);
  const [modalBarWidths, setModalBarWidths] = useState({
    communication_score: 0,
    analytical_score: 0,
    confidence_score: 0,
    balance_score: 0,
  });
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // Refs
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const toastTimerRef = useRef(null);
  const globalAudioRef = useRef(null);

  // Speech States
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);  // silence-detection timer
  const SILENCE_MS = 4500;               // stop after 4.5 s of silence

  // ── Speech Recognition Setup ─────────────────────────────────────
  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;        // keep listening through pauses
    recognition.lang = "en-IN";
    recognition.interimResults = true;    // get partial words so we can reset silence timer

    recognition.onstart = () => {
      setIsListening(true);
      // Start the silence timer immediately; it will reset whenever words arrive
      resetSilenceTimer(recognition);
    };

    recognition.onend = () => {
      clearTimeout(silenceTimerRef.current);
      setIsListening(false);
    };

    recognition.onresult = (event) => {
      let finalText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalText += event.results[i][0].transcript + " ";
        }
      }
      if (finalText.trim()) {
        setCurrentAnswer((prev) => (prev ? `${prev} ${finalText.trim()}` : finalText.trim()));
        setHasSpoken(true);
      }
      // Any speech (interim or final) resets the silence timer
      resetSilenceTimer(recognition);
    };

    recognition.onerror = (event) => {
      // 'no-speech' is normal — let the silence timer handle it; ignore the error
      if (event.error !== "no-speech") {
        console.warn("Speech recognition error:", event.error);
      }
      clearTimeout(silenceTimerRef.current);
      setIsListening(false);
    };

    recognitionRef.current = recognition;
  }, []);

  const resetSilenceTimer = (recognition) => {
    clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = setTimeout(() => {
      try { recognition.stop(); } catch (e) { /* already stopped */ }
    }, SILENCE_MS);
  };

  const handleMicClick = () => {
    const recognition = recognitionRef.current;

    if (!recognition) {
      alert("Speech recognition is not supported in this browser. Please use Google Chrome or Safari.");
      return;
    }

    if (isListening) {
      clearTimeout(silenceTimerRef.current);
      recognition.stop();
      return;
    }

    // Pause active audio playback if user starts speaking
    if (globalAudioRef.current) {
      globalAudioRef.current.pause();
      setCurrentPlayingId(null);
    }

    try {
      recognition.start();
    } catch (e) {
      console.warn("Speech recognition start failed:", e);
    }
  };

  // ── Clean up audio and timers on unmount ────────────────────────
  useEffect(() => {
    return () => {
      clearTimeout(silenceTimerRef.current);
      if (globalAudioRef.current) {
        globalAudioRef.current.pause();
        globalAudioRef.current = null;
      }
    };
  }, []);

  // ── Audio Playback Control ───────────────────────────────────────
  const togglePlayAudio = async (url, messageId) => {
    if (!url) return;

    if (globalAudioRef.current) {
      globalAudioRef.current.pause();
      if (currentPlayingId === messageId) {
        setCurrentPlayingId(null);
        globalAudioRef.current = null;
        return;
      }
    }

    // Resolve relative URL against baseUrl if needed
    let cleanUrl = url;
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      const baseDomain = baseUrl.replace(/(https?:\/\/[^\/]+).*/, "$1");
      if (url.startsWith("/secure-api")) {
        cleanUrl = baseDomain + url;
      } else if (url.startsWith("secure-api")) {
        cleanUrl = baseDomain + "/" + url;
      } else {
        const base = baseUrl.endsWith("/") ? baseUrl : baseUrl + "/";
        cleanUrl = base + (url.startsWith("/") ? url.substring(1) : url);
      }
    }

    if (cleanUrl.includes("://")) {
      const parts = cleanUrl.split("://");
      cleanUrl = parts[0] + "://" + parts[1].replace(/\/+/g, "/");
    } else {
      cleanUrl = cleanUrl.replace(/\/+/g, "/");
    }

    try {
      setCurrentPlayingId(messageId);

      // Fetch audio file content directly as a blob to bypass range request issues
      const res = await fetch(cleanUrl);
      if (!res.ok) throw new Error(`HTTP error ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);

      const audio = new Audio(blobUrl);
      globalAudioRef.current = audio;

      audio.play().catch((err) => {
        console.warn("Audio playback failed or blocked:", err);
        setCurrentPlayingId(null);
        globalAudioRef.current = null;
        URL.revokeObjectURL(blobUrl);
      });

      audio.onended = () => {
        if (globalAudioRef.current === audio) {
          setCurrentPlayingId(null);
          globalAudioRef.current = null;
          URL.revokeObjectURL(blobUrl);
        }
      };

      audio.onerror = (e) => {
        console.warn("Audio element error during playback:", e);
        if (globalAudioRef.current === audio) {
          setCurrentPlayingId(null);
          globalAudioRef.current = null;
          URL.revokeObjectURL(blobUrl);
        }
      };
    } catch (e) {
      console.error("Audio fetching/playback error:", e);
      setCurrentPlayingId(null);
      globalAudioRef.current = null;
    }
  };

  const toggleMute = () => {
    setIsMuted((prev) => {
      const nextMuted = !prev;
      if (nextMuted && globalAudioRef.current) {
        globalAudioRef.current.pause();
        setCurrentPlayingId(null);
      }
      return nextMuted;
    });
  };

  // ── Scroll to Bottom ─────────────────────────────────────────────
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  // ── Toast Utility ────────────────────────────────────────────────
  const triggerToast = (msg) => {
    setToastMessage(msg);
    setShowToast(true);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setShowToast(false);
    }, 3800);
  };

  // ── Escape Key for Modal Close ──────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleCopySessionId = () => {
    if (!sessionId) return;
    navigator.clipboard.writeText(sessionId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── API Wrapper Helper ───────────────────────────────────────────
  const callApi = async (path, payload) => {
    const base = baseUrl.replace(/\/$/, "");
    const res = await fetch(`${base}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(err.detail || "Server request failed");
    }
    return res.json();
  };

  // ── Start Interview ──────────────────────────────────────────────
  const startInterview = async () => {
    if (!profile.mail.trim() || !profile.candidateName.trim()) {
      triggerToast("Email and candidate name are required.");
      return;
    }

    setIsTyping(true);
    setInterviewActive(true);

    try {
      const data = await callApi("/upsc/interview/start", {
        mail: profile.mail.trim(),
        candidate_name: profile.candidateName.trim(),
        hometown: profile.hometown.trim() || null,
        graduation_subject: profile.graduationSubject.trim() || null,
        optional_subject: profile.optionalSubject.trim() || null,
        work_experience: profile.workExperience.trim() || null,
        hobbies: profile.hobbies.trim() || null,
      });

      setIsTyping(false);
      setSessionId(data.session_id);
      setCurrentStage(data.stage);
      setQuestionCount(1);
      
      const msgId = Date.now();
      setMessages([
        {
          id: msgId,
          type: "board",
          interviewer: data.interviewer || "Board",
          stage: data.stage,
          qType: "PRIMARY",
          content: data.question,
          count: 1,
          audioPath: data.audio_path || null,
        },
      ]);

      if (!isMuted && data.audio_path) {
        togglePlayAudio(data.audio_path, msgId);
      }

      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (err) {
      setIsTyping(false);
      setInterviewActive(false);
      triggerToast(err.message);
    }
  };

  // ── Submit Answer ────────────────────────────────────────────────
  const submitAnswer = async () => {
    const answer = currentAnswer.trim();
    if (!answer || !sessionId) return;

    // Add user response to chat
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        type: "candi",
        content: answer,
      },
    ]);
    
    setCurrentAnswer("");
    setHasSpoken(false); // reset voice gate for next question
    setIsTyping(true);

    try {
      const data = await callApi("/upsc/interview/answer", {
        session_id: sessionId,
        answer,
      });

      setIsTyping(false);
      setCurrentStage(data.stage);
      setQuestionCount((prev) => prev + 1);

      const msgId = Date.now();
      setMessages((prev) => [
        ...prev,
        {
          id: msgId,
          type: "board",
          interviewer: data.interviewer || "Board",
          stage: data.stage,
          qType: data.question_type,
          content: data.question,
          count: questionCount + 1,
          audioPath: data.audio_path || null,
        },
      ]);

      if (!isMuted && data.audio_path) {
        togglePlayAudio(data.audio_path, msgId);
      }

      if (data.stage === "FINAL" || data.question_type === "FINAL") {
        setCompleted();
      } else {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    } catch (err) {
      setIsTyping(false);
      triggerToast(err.message);
    }
  };

  // ── End Interview (Early or Normal) ──────────────────────────────
  const endInterview = () => {
    if (!sessionId) return;
    setIsConfirmOpen(true);
  };

  const handleConfirmEnd = () => {
    setIsConfirmOpen(false);
    setCompleted();
  };

  const handleCancelEnd = () => {
    setIsConfirmOpen(false);
  };

  const setCompleted = () => {
    if (globalAudioRef.current) {
      globalAudioRef.current.pause();
      globalAudioRef.current = null;
      setCurrentPlayingId(null);
    }
    setInterviewActive(false);
    setIsCompleted(true);
  };

  // ── Open Evaluation Modal ────────────────────────────────────────
  const openEvalModal = async () => {
    setIsModalOpen(true);
    setModalLoading(true);
    setEvalData(null);

    try {
      const data = await callApi("/upsc/interview/end", {
        session_id: sessionId,
      });
      const evaluation = data.evaluation || {};
      setEvalData(evaluation);
      setModalLoading(false);

      // Trigger width animation frame
      requestAnimationFrame(() => {
        setModalBarWidths({
          communication_score: (evaluation.communication_score || 0) * 10,
          analytical_score: (evaluation.analytical_score || 0) * 10,
          confidence_score: (evaluation.confidence_score || 0) * 10,
          balance_score: (evaluation.balance_score || 0) * 10,
        });
      });
    } catch (err) {
      setModalLoading(false);
      triggerToast(err.message);
    }
  };

  // ── Close Modal ──────────────────────────────────────────────────
  const closeModal = () => {
    setIsModalOpen(false);
    setModalBarWidths({
      communication_score: 0,
      analytical_score: 0,
      confidence_score: 0,
      balance_score: 0,
    });
  };

  // ── Input Key Handler ────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submitAnswer();
    }
  };

  // ── Auto-adjust Textarea Height ──────────────────────────────────
  const handleInput = (e) => {
    const el = e.target;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  return (
    <section className="chat_layout">
      {/* ── LEFT PANEL: PROFILE & STAGES ── */}
      <aside className="nav_context_container_interview">
        <section className="nav_learn_title_interview">
          <h4>Candidate Context</h4>
          <h6>Configure your DAF to begin simulation</h6>
        </section>

        <section className="profile_scroll_area">
          <span className="section_label_interview">Candidate Profile</span>
          
          <div className="form_area_interview">
            {/* <div className="field_interview">
              <label>Email *</label>
              <input
                type="email"
                placeholder="candidate@example.com"
                value={profile.mail}
                onChange={(e) =>
                  setProfile({ ...profile, mail: e.target.value })
                }
                disabled={interviewActive || isCompleted}
              />
            </div> */}
            
            <div className="field_interview">
              <label>Full Name *</label>
              <input
                type="text"
                placeholder="Arjun Sharma"
                value={profile.candidateName}
                onChange={(e) =>
                  setProfile({ ...profile, candidateName: e.target.value })
                }
                disabled={interviewActive || isCompleted}
              />
            </div>
            
            <div className="field_interview">
              <label>Hometown *</label>
              <input
                type="text"
                placeholder="Chennai, Tamil Nadu"
                value={profile.hometown}
                onChange={(e) =>
                  setProfile({ ...profile, hometown: e.target.value })
                }
                disabled={interviewActive || isCompleted}
              />
            </div>
            
            <div className="field_interview">
              <label>Graduation Subject *</label>
              <input
                type="text"
                placeholder="Computer Science"
                value={profile.graduationSubject}
                onChange={(e) =>
                  setProfile({
                    ...profile,
                    graduationSubject: e.target.value,
                  })
                }
                disabled={interviewActive || isCompleted}
              />
            </div>
            
            <div className="field_interview">
              <label>Optional Subject</label>
              <input
                type="text"
                placeholder="Public Administration"
                value={profile.optionalSubject}
                onChange={(e) =>
                  setProfile({ ...profile, optionalSubject: e.target.value })
                }
                disabled={interviewActive || isCompleted}
              />
            </div>
            
            <div className="field_interview">
              <label>Work Experience</label>
              <textarea
                placeholder="3 years in software engineering"
                value={profile.workExperience}
                onChange={(e) =>
                  setProfile({ ...profile, workExperience: e.target.value })
                }
                disabled={interviewActive || isCompleted}
              />
            </div>
            
            <div className="field_interview">
              <label>Hobbies *</label>
              <textarea
                placeholder="Reading, chess, trekking"
                value={profile.hobbies}
                onChange={(e) =>
                  setProfile({ ...profile, hobbies: e.target.value })
                }
                disabled={interviewActive || isCompleted}
              />
            </div>

            {!interviewActive && !isCompleted ? (
              <button
                className="btn_interview btn_primary_interview"
                onClick={startInterview}
                disabled={isTyping || !profile.candidateName.trim() || !profile.hometown.trim() || !profile.graduationSubject.trim() || !profile.hobbies.trim()}
              >
                {isTyping ? "Starting Simulator..." : "Begin Interview"}
              </button>
            ) : (
              <button
                className="btn_interview btn_danger_interview"
                onClick={endInterview}
                disabled={isCompleted}
              >
                {isCompleted ? "Completed" : "End & Evaluate"}
              </button>
            )}
          </div>

          {/* Session Badge */}
          {sessionId && (
            <div className="session_badge_interview">
              <div className="session_badge_header">
                <span className="s_label">Active Session</span>
                <div className="s_stage">
                  {!isCompleted && <span className="stage_dot_interview"></span>}
                  <span>
                    {isCompleted
                      ? "Completed"
                      : currentStage?.replace(/_/g, " ") || "Processing"}
                  </span>
                </div>
              </div>
              <div className="session_id_row">
                <code className="s_id_text" title={sessionId}>
                  {sessionId.slice(0, 8)}...{sessionId.slice(-8)}
                </code>
                <button
                  className="btn_copy_session"
                  onClick={handleCopySessionId}
                  title="Copy Session ID"
                >
                  {copied ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  )}
                  <span>{copied ? "Copied" : "Copy"}</span>
                </button>
              </div>
            </div>
          )}

          {/* Interview Stages Progress */}
          <span className="section_label_interview">Interview Stages</span>
          <div className="stage_track_interview">
            {STAGES.map((s, idx) => {
              const currentIdx = STAGES.findIndex((x) => x.key === currentStage);
              let stageStatus = "";
              if (isCompleted) {
                stageStatus = "done";
              } else if (currentIdx !== -1) {
                if (idx < currentIdx) stageStatus = "done";
                else if (idx === currentIdx) stageStatus = "active";
              }

              return (
                <div
                  key={s.key}
                  className={`stage_item_interview ${stageStatus}`}
                >
                  <div className="stage_num_interview">{idx + 1}</div>
                  <span>{s.label}</span>
                </div>
              );
            })}
          </div>
        </section>
      </aside>

      {/* ── CENTRAL PANEL: CHAT SCREEN ── */}
      <main className="chat_panel_interview">
        <header className="chat_header_interview">
          <h3>UPSC Interview Simulator</h3>
          
          <button
            className={`audio_toggle_header ${isMuted ? "muted" : ""}`}
            onClick={toggleMute}
            title={isMuted ? "Unmute Voice" : "Mute Voice"}
          >
            {isMuted ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                <line x1="1" y1="1" x2="23" y2="23"></line>
                <path d="M9 9v6a3 3 0 0 0 3 3h1.586l4.707 4.707A1 1 0 0 0 20 22V4a1 1 0 0 0-1.707-.707L13.586 8H12a3 3 0 0 0-3 3z"></path>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path>
              </svg>
            )}
            <span>{isMuted ? "Muted" : "Voice On"}</span>
          </button>
        </header>

        {/* Messages Body */}
        <div className="chat_body_interview">
          {messages.length === 0 && !interviewActive ? (
            <div className="empty_state_interview">
              <div className="hero_card_interview">
                <div className="hero_icon_container">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="hero_icon_svg">
                    <path d="m16 16 3-8 3 8c-.87.65-2.24 1-3 1s-2.13-.35-3-1Z" />
                    <path d="m2 16 3-8 3 8c-.87.65-2.24 1-3 1s-2.13-.35-3-1Z" />
                    <path d="M7 21h10" />
                    <path d="M12 3v18" />
                    <path d="M3 7h18" />
                  </svg>
                </div>
                <h2>UPSC Mock Interview Simulator</h2>
                <p className="hero_subtitle">
                  Practice with an AI-powered board that simulates the rigorous UPSC Civil Services personality test. Get real-time situational, DAF-based, and current affairs questions.
                </p>

                <div className="hero_features_grid">
                  <div className="hero_feature_item">
                    <div className="feature_icon_wrap">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10 9 9 9 8 9" />
                      </svg>
                    </div>
                    <div className="feature_text">
                      <h4>DAF-Based Profiling</h4>
                      <p>Questions are dynamically tailored using your graduation subject, hometown, optional subject, work experience, and hobbies.</p>
                    </div>
                  </div>

                  <div className="hero_feature_item">
                    <div className="feature_icon_wrap">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                      </svg>
                    </div>
                    <div className="feature_text">
                      <h4>6-Stage Progressive Flow</h4>
                      <p>Simulates the exact interview sequence: Introduction, DAF Deep Dive, Current Affairs, Situational scenarios, Counter-questioning, and Final summary.</p>
                    </div>
                  </div>

                  <div className="hero_feature_item">
                    <div className="feature_icon_wrap">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </div>
                    <div className="feature_text">
                      <h4>Real-time Pace & Timer</h4>
                      <p>Simulates board interactions with real-time response capture, keeping you engaged under natural pacing constraints.</p>
                    </div>
                  </div>

                  <div className="hero_feature_item">
                    <div className="feature_icon_wrap">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    </div>
                    <div className="feature_text">
                      <h4>Instant Detailed Feedback</h4>
                      <p>Get graded on Communication, Analytical ability, Confidence, and Balance with actionable, comprehensive strengths and improvements feedback.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            messages.map((msg) => {
              if (msg.type === "board") {
                return (
                  <div key={msg.id} className="msg_interview board_sender">
                    <div className="msg_meta_interview">
                      {msg.interviewer} · Q{msg.count}
                    </div>
                    <div className="bubble_interview">
                      <div className="bubble_header_interview">
                        <span className="stage_tag_interview">
                          {msg.stage?.replace(/_/g, " ")}
                          {msg.qType && msg.qType !== "PRIMARY"
                            ? ` · ${msg.qType}`
                            : ""}
                        </span>

                        {msg.audioPath && (
                          <div className="bubble_audio_wrapper">
                            {currentPlayingId === msg.id && (
                              <div className="sound_wave_animation">
                                <span className="wave_bar"></span>
                                <span className="wave_bar"></span>
                                <span className="wave_bar"></span>
                                <span className="wave_bar"></span>
                              </div>
                            )}
                            <button
                              className={`bubble_audio_control ${currentPlayingId === msg.id ? "playing" : ""}`}
                              onClick={() => togglePlayAudio(msg.audioPath, msg.id)}
                              title={currentPlayingId === msg.id ? "Pause Voice" : "Play Voice"}
                            >
                              {currentPlayingId === msg.id ? (
                                <svg viewBox="0 0 24 24" fill="currentColor" width="10" height="10">
                                  <rect x="4" y="4" width="4" height="16" rx="1"></rect>
                                  <rect x="14" y="4" width="4" height="16" rx="1"></rect>
                                </svg>
                              ) : (
                                <svg viewBox="0 0 24 24" fill="currentColor" width="10" height="10">
                                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                </svg>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                      <div
                        className="bubble_text_content"
                        style={{ whiteSpace: "pre-wrap" }}
                      >
                        {msg.content}
                      </div>
                    </div>
                  </div>
                );
              } else {
                return (
                  <div key={msg.id} className="msg_interview candi_sender">
                    <div className="msg_meta_interview">You</div>
                    <div className="bubble_interview">
                      <div
                        className="bubble_text_content"
                        style={{ whiteSpace: "pre-wrap" }}
                      >
                        {msg.content}
                      </div>
                    </div>
                  </div>
                );
              }
            })
          )}

          {isTyping && (
            <div className="typing_interview">
              <span></span>
              <span></span>
              <span></span>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Input Bar / Feedback Trigger */}
        <footer className="chat_footer_interview">
          {!isCompleted ? (
            <div className="input_bar_interview">
              {interviewActive && (
                <button
                  className={`btn_mic_interview ${isListening ? "listening" : (!hasSpoken && interviewActive && !isTyping ? "voice_prompt" : "")}`}
                  onClick={handleMicClick}
                  disabled={isTyping}
                  title={isListening ? "Stop Listening" : "Speak Answer"}
                >
                  {isListening ? (
                    <div className="listening_pulse"></div>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                      <line x1="12" y1="19" x2="12" y2="23"></line>
                      <line x1="8" y1="23" x2="16" y2="23"></line>
                    </svg>
                  )}
                </button>
              )}
              <textarea
                ref={inputRef}
                placeholder={
                  !interviewActive
                    ? "Configure profile and click Begin Interview to start..."
                    : isListening
                    ? "Listening... Speak your answer now."
                    : hasSpoken
                    ? "Review or edit your answer, then click Send…"
                    : "🎙️ Press the mic button to speak your answer first"
                }
                value={currentAnswer}
                onChange={(e) => setCurrentAnswer(e.target.value)}
                onKeyDown={handleKeyDown}
                onInput={handleInput}
                disabled={!interviewActive || isTyping || isListening || !hasSpoken}
                rows={1}
                style={{
                  cursor: (interviewActive && !hasSpoken && !isListening && !isTyping)
                    ? "not-allowed"
                    : undefined,
                  opacity: (interviewActive && !hasSpoken && !isListening && !isTyping)
                    ? 0.55
                    : 1,
                }}
              />
              <button
                className="btn_interview btn_send_interview"
                onClick={submitAnswer}
                disabled={!interviewActive || isTyping || !currentAnswer.trim() || isListening || !hasSpoken}
              >
                Send
              </button>
            </div>
          ) : (
            <div className="feedback_bar_interview">
              <button
                className="btn_feedback_interview"
                onClick={openEvalModal}
              >
                <img
                  src={assets.ic_descriptive}
                  alt="feedback"
                  style={{ width: "18px", height: "18px", filter: "brightness(0) invert(1)" }}
                />{" "}
                Get Feedback
              </button>
            </div>
          )}
        </footer>
      </main>

      {/* ── CONFIRM END MODAL ── */}
      {isConfirmOpen && (
        <div className="confirm_backdrop_interview" onClick={handleCancelEnd}>
          <div className="confirm_modal_interview" onClick={(e) => e.stopPropagation()}>
            <div className="confirm_icon_wrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="28" height="28">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <h3 className="confirm_title_interview">End Interview?</h3>
            <p className="confirm_desc_interview">
              You're about to end the session early. Your responses will be evaluated and a detailed performance report will be generated.
            </p>
            <div className="confirm_actions_interview">
              <button className="btn_confirm_cancel" onClick={handleCancelEnd}>
                Continue Interview
              </button>
              <button className="btn_confirm_ok" onClick={handleConfirmEnd}>
                Yes, End &amp; Evaluate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── TOAST ── */}
      <div className={`toast_interview ${showToast ? "show" : ""}`}>
        {toastMessage}
      </div>

      {/* ── EVALUATION MODAL ── */}
      <div
        className={`modal_backdrop_interview ${isModalOpen ? "open" : ""}`}
        onClick={(e) => {
          if (e.target.classList.contains("modal_backdrop_interview")) {
            closeModal();
          }
        }}
      >
        <div
          className="modal_interview"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title-interview"
        >
          <div className="modal_header_interview">
            <div className="modal_header_left_interview">
              <div className="modal_eyebrow_interview">Evaluation System</div>
              <div className="modal_title_interview" id="modal-title-interview">
                Interview Performance
              </div>
            </div>
            <button className="modal_close_interview" onClick={closeModal}>
              ✕
            </button>
          </div>

          <div className="modal_body_interview">
            {modalLoading ? (
              <div className="modal_loading_interview">
                <div className="typing_indicator_modal">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <p>Generating evaluation response from server...</p>
              </div>
            ) : evalData ? (
              <>
                <div className="modal_scores_interview">
                  {[
                    { label: "Communication", key: "communication_score" },
                    { label: "Analytical", key: "analytical_score" },
                    { label: "Confidence", key: "confidence_score" },
                    { label: "Balance", key: "balance_score" },
                  ].map((s) => (
                    <div key={s.key} className="modal_score_item_interview">
                      <label>{s.label}</label>
                      <div className="modal_score_row_interview">
                        <div className="modal_bar_wrap_interview">
                          <div
                            className="modal_bar_interview"
                            style={{ width: `${modalBarWidths[s.key]}%` }}
                          ></div>
                        </div>
                        <div className="modal_score_num_interview">
                          {evalData[s.key] ?? "—"}
                          <span>/10</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="modal_divider_interview"></div>

                {evalData.overall_feedback && (
                  <div className="modal_section_interview">
                    <h4>Overall Feedback</h4>
                    <p>{evalData.overall_feedback}</p>
                  </div>
                )}

                {evalData.strengths && (
                  <div className="modal_section_interview">
                    <h4>Strengths</h4>
                    <p>{evalData.strengths}</p>
                  </div>
                )}

                {evalData.improvements && (
                  <div className="modal_section_interview">
                    <h4>Areas to Improve</h4>
                    <p>{evalData.improvements}</p>
                  </div>
                )}

                {!evalData.overall_feedback &&
                  !evalData.strengths &&
                  !evalData.improvements && (
                    <div className="modal_section_interview">
                      <h4>Raw Evaluation Response</h4>
                      <pre
                        style={{
                          fontSize: "11px",
                          background: "#f1f5f9",
                          padding: "12px",
                          borderRadius: "8px",
                          overflowX: "auto",
                          color: "#334155",
                        }}
                      >
                        {JSON.stringify(evalData, null, 2)}
                      </pre>
                    </div>
                  )}
              </>
            ) : (
              <div style={{ textAlign: "center", color: "#ef4444" }}>
                Failed to retrieve evaluation results. Please try again.
              </div>
            )}
          </div>

          <div className="modal_footer_interview">
            <button
              className="btn_modal_close_interview"
              onClick={closeModal}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
